/**
 * The console's REST client (ADR-0006).
 *
 * The access token lives in a module variable and nowhere else — not
 * localStorage, not sessionStorage. A reload loses it and the app silently
 * obtains a new one from the httpOnly refresh cookie, which JavaScript cannot
 * read and therefore cannot leak.
 */

const BASE = '/api/v1';

/**
 * Sent on every request so the server can refuse a build that is too old
 * (launch gate 11). Defined by Vite at build time from the package version.
 *
 * The console is far less exposed to this than the mobile app — a browser
 * fetches the current bundle on reload, so "old build in the field" is at most
 * a tab someone left open over a deploy. The header is sent anyway, because the
 * server-side gate is one rule and a client that opts out of it is a client
 * nobody can reason about.
 */
const CLIENT_VERSION: string =
  (typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : undefined) ?? '0.0.0';
const CLIENT_PLATFORM = 'web';

let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function hasAccessToken(): boolean {
  return accessToken !== null;
}

export function setSessionLostHandler(handler: (() => void) | null): void {
  onSessionLost = handler;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Publishing and revoking require a recent sign-in. */
  get needsReauth(): boolean {
    return this.code === 'reauthentication_required';
  }

  get isOffline(): boolean {
    return this.status === 0;
  }

  /**
   * This build is too old for the server. 426, not 401 — a 401 would be taken
   * as an expired session and sent round the refresh path, which cannot help.
   */
  get needsAppUpdate(): boolean {
    return this.code === 'client_update_required';
  }

  /** Validation failures carry a list the form can render beside its fields. */
  get problems(): string[] {
    const raw = this.details.problems;
    if (!Array.isArray(raw)) return [];
    return raw.map((entry) =>
      typeof entry === 'string'
        ? entry
        : `${(entry as { field?: string }).field ?? ''} ${(entry as { issue?: string }).issue ?? ''}`.trim(),
    );
  }
}

/**
 * Endpoints that establish identity. A 401 from one of these is an answer —
 * "wrong password", "enter your code" — not an expired session, and running
 * them through the refresh path signs people out mid-login.
 */
const IDENTITY_PATHS = ['/auth/login', '/auth/mfa/verify', '/auth/refresh'];

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Exchange the refresh cookie for a new access token.
 *
 * Concurrent 401s share one attempt: rotating the refresh token twice for the
 * same expiry looks exactly like token theft to the server, which would revoke
 * the whole family and sign the user out for loading two panels at once.
 */
export async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-client-version': CLIENT_VERSION,
          'x-client-platform': CLIENT_PLATFORM,
        },
        credentials: 'same-origin',
        body: '{}',
      });
      if (!response.ok) {
        accessToken = null;
        return false;
      }
      const body = (await response.json()) as { access_token: string };
      accessToken = body.access_token;
      return true;
    } catch {
      // A network failure is not a revoked session. Keep what we have and let
      // the caller surface an offline state.
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

type Options = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
};

export async function request<T>(path: string, options: Options = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;
  const isIdentity = IDENTITY_PATHS.some((prefix) => path.startsWith(prefix));

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {
      accept: 'application/json',
      'x-client-version': CLIENT_VERSION,
      'x-client-platform': CLIENT_PLATFORM,
    };
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (accessToken && !isIdentity) headers.authorization = `Bearer ${accessToken}`;
    return fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  };

  let response: Response;
  try {
    response = await send();
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    throw new ApiError(0, 'offline', 'Cannot reach the server. Check your connection.');
  }

  // Ahead of the 401 path so the two are never confused: refreshing would
  // spend a refresh token on a session this build cannot use, and drop the
  // administrator on the sign-in screen with no explanation.
  if (response.status === 426) {
    const text = await response.text();
    const payload: unknown = text ? JSON.parse(text) : {};
    const error = (payload as { error?: Record<string, unknown> }).error ?? {};
    throw new ApiError(
      426,
      'client_update_required',
      (error.message as string) ??
        'This version of the console is out of date. Reload the page to continue.',
      (error.details as Record<string, unknown>) ?? {},
    );
  }

  if (response.status === 401 && !isIdentity) {
    const recovered = await refreshSession();
    if (recovered) {
      try {
        response = await send();
      } catch {
        throw new ApiError(0, 'offline', 'Cannot reach the server. Check your connection.');
      }
    } else {
      onSessionLost?.();
    }
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : {};

  if (!response.ok) {
    const error = (payload as { error?: Record<string, unknown> }).error ?? {};
    throw new ApiError(
      response.status,
      (error.code as string) ?? 'error',
      (error.message as string) ?? 'Something went wrong',
      (error.details as Record<string, unknown>) ?? {},
    );
  }

  return payload as T;
}

export const api = {
  get: <T,>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),
};
